import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Button, Box, Typography } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Componente que muestra una notificación profesional cuando hay
 * una nueva versión de la PWA disponible
 */
export default function UpdateNotification() {
    const [show, setShow] = useState(false);
    const [registration, setRegistration] = useState(null);

    useEffect(() => {
        const handleUpdate = (event) => {
            const reg = event.detail.registration;
            console.log('UpdateNotification: Nueva versión detectada');
            setRegistration(reg);
            setShow(true);
        };

        window.addEventListener('sw-update-available', handleUpdate);

        return () => {
            window.removeEventListener('sw-update-available', handleUpdate);
        };
    }, []);

    const handleUpdate = () => {
        if (!registration) {
            console.error('No hay registration disponible');
            return;
        }

        // Buscar el worker waiting (el que tiene la nueva versión)
        const waitingWorker = registration.waiting;

        if (!waitingWorker) {
            console.error('No hay worker waiting. Estado:', {
                installing: registration.installing,
                waiting: registration.waiting,
                active: registration.active
            });
            // Intentar recargar directamente
            window.location.reload();
            return;
        }

        console.log('Enviando SKIP_WAITING al service worker...');

        // Primero configurar el listener ANTES de enviar el mensaje
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('Controller cambiado, recargando página...');
            window.location.reload();
        }, { once: true });

        // Ahora sí, decir al nuevo SW que tome control
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    };

    const handleDismiss = () => {
        setShow(false);
    };

    return (
        <Snackbar
            open={show}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{
                bottom: { xs: 80, sm: 24 },
                '& .MuiSnackbarContent-root': {
                    padding: 0,
                },
            }}
        >
            <Alert
                severity="info"
                icon={false}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '90vw', sm: 500 },
                    backgroundColor: '#fff',
                    color: '#212529',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    border: '2px solid #cc071e',
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                        width: '100%',
                        padding: 0,
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                backgroundColor: '#fee',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <SystemUpdateAltIcon
                                sx={{
                                    fontSize: 24,
                                    color: '#cc071e',
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 16,
                                    fontWeight: 700,
                                    color: '#212529',
                                    lineHeight: 1.3,
                                }}
                            >
                                Nueva versión disponible
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#6c757d',
                                    lineHeight: 1.4,
                                    mt: 0.25,
                                }}
                            >
                                Actualiza para obtener las últimas mejoras
                            </Typography>
                        </Box>
                    </Box>

                    {/* Botones */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 2,
                        }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleDismiss}
                            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                flex: 1,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 13,
                                color: '#6c757d',
                                borderColor: '#dee2e6',
                                '&:hover': {
                                    borderColor: '#6c757d',
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                },
                            }}
                        >
                            Después
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleUpdate}
                            startIcon={<SystemUpdateAltIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                flex: 1,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 13,
                                backgroundColor: '#cc071e',
                                '&:hover': {
                                    backgroundColor: '#a30618',
                                },
                            }}
                        >
                            Actualizar
                        </Button>
                    </Box>
                </Box>
            </Alert>
        </Snackbar>
    );
}
